// ProjectsPage.js
import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import T from '../../components/common/T';
import ProjectList from "./ProjectListView";
import ProjectKanban from "./ProjectKanbanView";
import { useProjects } from "../../context/ProjectContext";
import { useEmployees } from "../../context/EmployeeContext";
import PageLayout, { PageHeader, StatsGrid, Toolbar, FilterPanel, TableCard } from "../../components/common/PageLayout";
import ViewSwitcher from "../../components/common/ViewSwitcher";
import { TopPrimaryButton } from "../../components/common/StandardButtons";
import useModuleFilters from "../../hooks/useModuleFilters";
import {
  FolderOpenIcon, CheckCircleIcon, ClockIcon,
  ArrowPathIcon
} from "@heroicons/react/24/outline";

const ProjectsPage = () => {
  const [view, setView] = useState("list");
  const [filters, setFilters] = useState({ status: "", price: "", dateSort: "" });
  const [searchTerm, setSearchTerm] = useState("");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newProjectData, setNewProjectData] = useState({ name: "", managerId: "" });
  const [isCreating, setIsCreating] = useState(false);

  const {
    showFilter,
    setShowFilter,
    toggleFilters,
    filterOptions,
    loadingFilters,
    clearFilters,
    clearingFilters,
  } = useModuleFilters("projects", {
    statuses: ['Pending', 'In Progress', 'Completed', 'On Hold'],
  });

  const navigate = useNavigate();
  const { projects = [], addProject, fetchProjects } = useProjects();
  const { employees } = useEmployees();
  const filterRef = useRef(null);

  // Fetch projects on mount when navigating to this page
  useEffect(() => {
    if (typeof fetchProjects === 'function') {
      fetchProjects();
    }
  }, [fetchProjects]);

  useEffect(() => {
    const handleClick = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) setShowFilter(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleClearAll = () => {
    setFilters({ status: "", price: "", dateSort: "" });
    setSearchTerm("");
    if (typeof fetchProjects === 'function') fetchProjects();
  };

  const filteredProjects = useMemo(() => {
    let result = [...projects];
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      result = result.filter(p => p.name?.toLowerCase().includes(s) || p.client?.toLowerCase().includes(s));
    }
    if (filters.status) result = result.filter(p => p.status?.toLowerCase() === filters.status.toLowerCase());
    if (filters.price === "low-high") result.sort((a, b) => (a.budget || 0) - (b.budget || 0));
    else if (filters.price === "high-low") result.sort((a, b) => (b.budget || 0) - (a.budget || 0));
    if (filters.dateSort === "newest") result.sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
    else if (filters.dateSort === "oldest") result.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
    return result;
  }, [projects, searchTerm, filters]);

  // ── Stats derived from ALL projects (not filtered) ──────────────────────────
  const stats = useMemo(() => [
    {
      label: "Total Projects",
      value: projects.length,
      icon: FolderOpenIcon,
      variant: "default",
      isActive: !filters.status || filters.status === "",
      onClick: () => setFilters(f => ({ ...f, status: "" })),
    },
    {
      label: "Pending",
      value: projects.filter(p => p.status === "Pending").length,
      icon: ClockIcon,
      variant: "orange",
      isActive: filters.status === "Pending",
      onClick: () => setFilters(f => ({ ...f, status: f.status === "Pending" ? "" : "Pending" })),
    },
    {
      label: "In Progress",
      value: projects.filter(p => p.status === "In Progress").length,
      icon: ArrowPathIcon,
      variant: "blue",
      isActive: filters.status === "In Progress",
      onClick: () => setFilters(f => ({ ...f, status: f.status === "In Progress" ? "" : "In Progress" })),
    },
    {
      label: "Completed",
      value: projects.filter(p => p.status === "Completed").length,
      icon: CheckCircleIcon,
      variant: "green",
      isActive: filters.status === "Completed",
      onClick: () => setFilters(f => ({ ...f, status: f.status === "Completed" ? "" : "Completed" })),
    },
  ], [projects, filters.status]);

  const handleCreateProjectSubmit = async (e) => {
    e.preventDefault();
    if (!newProjectData.name) return alert("Project Name is required");
    setIsCreating(true);
    const draftProject = {
      name: newProjectData.name,
      projectManager: newProjectData.managerId,
      stage: "Draft", status: "Pending", client: "", type: "Construction",
      description: "New project created via dashboard.", budget: 0, currency: "USD",
      startDate: new Date().toISOString().split('T')[0],
      constructionStages: [
        { name: "Draft", weight: 0, completed: 0 },
        { name: "Confirmed", weight: 0, completed: 0 },
        { name: "Internal Approved", weight: 0, completed: 0 },
        { name: "Govt. Approved", weight: 0, completed: 0 },
        { name: "In Progress", weight: 50, completed: 0 },
        { name: "Ready to Possession", weight: 20, completed: 0 },
        { name: "Ready to Move", weight: 30, completed: 0 },
      ],
    };
    try {
      const newId = await addProject(draftProject);
      setIsCreateModalOpen(false);
      setNewProjectData({ name: "", managerId: "" });
      if (newId) navigate(`/admin/project/${newId}`);
    } catch {
      alert("Failed to create project");
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <PageLayout>
      {/* Header */}
      <PageHeader
        icon={FolderOpenIcon}
        iconColor="text-[#E8612C]"
        title="Project Management"
        subtitle="Track construction projects, budgets & milestones"
        onRefresh={() => window.location.reload()}
      >
        <TopPrimaryButton
          onClick={() => setIsCreateModalOpen(true)}
          label="New Project"
        />
      </PageHeader>

      {/* Stats */}
      <StatsGrid stats={stats} cols={4} />

      {/* Toolbar */}
      <Toolbar
        searchTerm={searchTerm}
        onSearch={setSearchTerm}
        searchPlaceholder="Search projects..."
        onToggleFilters={toggleFilters}
        showFilters={showFilter}
      >
        <ViewSwitcher
          view={view}
          onViewChange={setView}
          availableViews={['list', 'kanban']}
        />
      </Toolbar>

      {/* Filter panel */}
      <FilterPanel
        isOpen={showFilter}
        loading={loadingFilters}
        isClearing={clearingFilters}
        onClear={handleClearAll}
      >
        <div>
          <label className="block text-xs text-gray-500 dark:text-[#848d97] mb-1 flex items-center justify-between">
            <span><T>Status</T></span>
            {loadingFilters && <span className="text-[10px] text-gray-400 animate-pulse font-normal">Loading...</span>}
          </label>
          <select 
            disabled={loadingFilters}
            value={filters.status} 
            onChange={e => setFilters(f => ({ ...f, status: e.target.value }))} 
            className="border border-gray-300 dark:border-[#30363d] rounded-lg p-2 w-full text-sm bg-white dark:bg-[#21262d] text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-[#E8612C]"
          >
            <option value=""><T>All Statuses</T></option>
            {(filterOptions.statuses || ['Pending', 'In Progress', 'Completed', 'On Hold']).map(st => (
              <option key={st} value={st}>{st}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 dark:text-[#848d97] mb-1"><T>Budget</T></label>
          <select 
            disabled={loadingFilters}
            value={filters.price} 
            onChange={e => setFilters(f => ({ ...f, price: e.target.value }))} 
            className="border border-gray-300 dark:border-[#30363d] rounded-lg p-2 w-full text-sm bg-white dark:bg-[#21262d] text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-[#E8612C]"
          >
            <option value=""><T>Default</T></option>
            <option value="low-high"><T>Low → High</T></option>
            <option value="high-low"><T>High → Low</T></option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 dark:text-[#848d97] mb-1"><T>Sort By Date</T></label>
          <select 
            disabled={loadingFilters}
            value={filters.dateSort} 
            onChange={e => setFilters(f => ({ ...f, dateSort: e.target.value }))} 
            className="border border-gray-300 dark:border-[#30363d] rounded-lg p-2 w-full text-sm bg-white dark:bg-[#21262d] text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-[#E8612C]"
          >
            <option value=""><T>Default</T></option>
            <option value="newest"><T>Newest First</T></option>
            <option value="oldest"><T>Oldest First</T></option>
          </select>
        </div>
      </FilterPanel>

      {/* Content */}
      <TableCard>
        {filteredProjects.length === 0 ? (
          <div className="py-10 text-center text-gray-500 dark:text-[#848d97] flex flex-col items-center gap-2">
            <FolderOpenIcon className="w-10 h-10 text-gray-300 dark:text-[#6e7681]" />
            <p><T>No projects found. Try adjusting filters.</T></p>
          </div>
        ) : view === "list" ? (
          <ProjectList projects={filteredProjects} />
        ) : (
          <ProjectKanban projects={filteredProjects} />
        )}
      </TableCard>

      {/* Create Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] p-6 rounded-xl w-full max-w-[400px] shadow-2xl">
            <h3 className="text-base font-bold text-gray-800 dark:text-[#f0f6fc] mb-4"><T>Start New Project</T></h3>
            <form onSubmit={handleCreateProjectSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-600 dark:text-[#848d97] mb-1"><T>Project Name</T> <span className="text-red-500">*</span></label>
                <input autoFocus type="text" className="w-full border border-gray-300 dark:border-[#30363d] bg-white dark:bg-[#21262d] text-gray-900 dark:text-white p-2 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                  placeholder="e.g. Sunrise Apartments" value={newProjectData.name}
                  onChange={e => setNewProjectData({ ...newProjectData, name: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-600 dark:text-[#848d97] mb-1"><T>Assign Manager</T></label>
                <select className="w-full border border-gray-300 dark:border-[#30363d] bg-white dark:bg-[#21262d] text-gray-900 dark:text-white p-2 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                  value={newProjectData.managerId}
                  onChange={e => setNewProjectData({ ...newProjectData, managerId: e.target.value })}>
                  <option value="">-- <T>Select Manager</T> --</option>
                  {employees && employees.map(emp => (
                    <option key={emp.id} value={emp.id} className="dark:bg-[#161b22]">{emp.name || emp.employeeName}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-400 dark:text-[#6e7681] mt-1"><T>If selected, they will receive an email alert.</T></p>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setIsCreateModalOpen(false)} className="flex-1 bg-gray-100 dark:bg-[#21262d] border border-gray-200 dark:border-[#30363d] text-gray-700 dark:text-[#848d97] py-2 rounded-lg font-bold hover:bg-gray-200 dark:hover:bg-[#30363d] transition"><T>Cancel</T></button>
                <button disabled={isCreating} type="submit" className="flex-1 bg-[#D9531E] hover:bg-[#C24514] active:scale-[0.98] text-white py-2 rounded-lg font-bold disabled:opacity-50 transition shadow-xs">
                  {isCreating ? "Creating..." : "Create Draft"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default ProjectsPage;